import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getMyStudent } from "@/lib/academy.functions";
import svgIcon from "@/assets/svg-icon.png.asset.json";
import { Home, BookOpen, Sparkles, LifeBuoy, LogOut } from "lucide-react";

export const Route = createFileRoute("/aluno")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Área do Aluno | Academia SVG" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AlunoLayout,
});

function AlunoLayout() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!alive) return;
      if (!data.user) {
        navigate({ to: "/entrar", replace: true });
        return;
      }
      setEmail(data.user.email ?? null);
      setChecking(false);
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  const { data: me } = useQuery({
    queryKey: ["academy", "me"],
    queryFn: () => getMyStudent(),
    enabled: !checking,
    staleTime: 60_000,
  });

  if (checking) {
    return (
      <div className="container-svg py-24 text-center text-sm text-muted-foreground">Carregando área do aluno…</div>
    );
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/entrar", replace: true });
  }

  const status = me?.student?.status;
  if (me?.student && status !== "approved") {
    return (
      <div className="container-svg py-16">
        <div className="mx-auto max-w-xl card-glass text-center">
          {status === "rejected" ? (
            <>
              <div className="text-3xl">⛔</div>
              <h1 className="mt-3 text-2xl font-bold">Cadastro não aprovado</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {me.student.rejection_reason || "Entre em contato com a equipe SVG para regularizar seu acesso."}
              </p>
            </>
          ) : (
            <>
              <div className="text-3xl">⏳</div>
              <h1 className="mt-3 text-2xl font-bold">Aguardando aprovação</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Sua conta foi criada com sucesso, <strong>{me.student.full_name || email}</strong>.
                A equipe SVG irá liberar seu acesso à Academia em breve (normalmente em poucas horas).
              </p>
            </>
          )}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a
              href="https://pixel.agendasistemacrm.com.br/r/qxgushz"
              target="_blank"
              rel="noopener"
              className="btn-primary text-sm"
            >
              Falar com a SVG no WhatsApp
            </a>
            <button onClick={signOut} className="btn-outline text-sm">Sair</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-background via-background to-muted">
      <div className="container-svg grid gap-6 py-8 md:grid-cols-[260px_1fr]">
        <aside className="h-fit rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-gradient-to-b from-ink/[0.05] to-ink/[0.01] p-5 md:sticky md:top-24">
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-primary/60 to-primary/10 blur-md" />
              <img
                src={svgIcon.url}
                alt="Academia SVG"
                className="relative h-20 w-20 rounded-full border-2 border-primary/60 bg-black object-cover"
              />
            </div>
            <div className="mt-3 text-sm font-semibold truncate max-w-[200px]">
              {me?.student?.full_name || email}
            </div>
            <div className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
              Membro Premium
            </div>

            <div className="mt-4 w-full">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>Pontos</span>
                <span className="font-bold text-primary">{me?.totalPoints ?? 0}</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/50"
                  style={{ width: `${Math.min(100, (me?.totalPoints ?? 0) / 10)}%` }}
                />
              </div>
            </div>
          </div>

          <nav className="mt-6 flex flex-col gap-1 text-sm">
            <SideLink to="/aluno" exact icon={Home}>Início</SideLink>
            <SideLink to="/aluno/cursos" icon={BookOpen}>Todos os módulos</SideLink>
            <SideLink to="/aluno/ofertas" icon={Sparkles}>Ofertas exclusivas</SideLink>
            <SideLink to="/aluno/ajuda" icon={LifeBuoy}>Central de ajuda</SideLink>
          </nav>

          <button
            onClick={signOut}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-ink/10 px-3 py-2 text-xs text-muted-foreground hover:bg-ink/[0.04]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sair
          </button>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SideLink({
  to,
  exact,
  icon: Icon,
  children,
}: {
  to: string;
  exact?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to as never}
      activeOptions={exact ? { exact: true } : undefined}
      activeProps={{
        className:
          "flex items-center gap-2.5 rounded-lg border-l-2 border-primary bg-primary/10 px-3 py-2.5 font-semibold text-foreground",
      }}
      inactiveProps={{
        className:
          "flex items-center gap-2.5 rounded-lg border-l-2 border-transparent px-3 py-2.5 text-muted-foreground transition-colors hover:bg-ink/[0.04] hover:text-foreground",
      }}
    >
      <Icon className="h-4 w-4" />
      {children}
    </Link>
  );
}